/**
 * Copyright (c) 2016-present GlossGenius. All rights reserved.
 */

import React, { ReactNode } from 'react';
import Link from 'next/link';

import cx from 'classnames';

import Spinner from 'components/Spinner';

import s from './Button.module.css';

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  rounded?: boolean;
  white?: boolean;
  children: ReactNode;
  loading?: boolean;
  to?: URL | string;
  newTab?: boolean;
  outline?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, Props>(
  (
    {
      className,
      rounded = true,
      white = false,
      children,
      loading = false,
      type = 'submit',
      to,
      disabled = false,
      outline = false,
      newTab,
      ...other
    },
    ref,
  ) => {
    const otherProps = { ...other };

    const classNames = cx(
      s.root,
      {
        [s.notRounded]: !rounded,
        [s.white]: white,
        [s.outline]: outline,
      },
      className,
    );

    const renderInsideButton = () => (
      <span>
        <span className={cx(loading ? s.visHid : '')}>{children}</span>
        <Spinner loading={loading} />
      </span>
    );

    if (to) {
      if (newTab) {
        return (
          <a
            href={to as string}
            className={classNames}
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        );
      }

      return (
        <Link href={to} className={classNames}>
          {children}
        </Link>
      );
    }

    return (
      <button
        // eslint-disable-next-line react/button-has-type
        type={type}
        className={classNames}
        {...otherProps}
        disabled={disabled}
        ref={ref}
      >
        {renderInsideButton()}
      </button>
    );
  },
);

Button.displayName = 'Button';

export default Button;
